/*
 * @author bai <ghimping@alpstein.my>
 * @link https://www.alpstein.my
 * @copyright Copyright (c) Alpstein Software
 *            _____                    _____                    _____
 *           /\    \                  /\    \                  /\    \
 *          /::\    \                /::\    \                /::\    \
 *         /::::\    \              /::::\    \               \:::\    \
 *        /::::::\    \            /::::::\    \               \:::\    \
 *       /:::/\:::\    \          /:::/\:::\    \               \:::\    \
 *      /:::/__\:::\    \        /:::/__\:::\    \               \:::\    \
 *     /::::\   \:::\    \      /::::\   \:::\    \              /::::\    \
 *    /::::::\   \:::\    \    /::::::\   \:::\    \    ____    /::::::\    \
 *   /:::/\:::\   \:::\ ___\  /:::/\:::\   \:::\    \  /\   \  /:::/\:::\    \
 *  /:::/__\:::\   \:::|    |/:::/  \:::\   \:::\____\/::\   \/:::/  \:::\____\
 *  \:::\   \:::\  /:::|____|\::/    \:::\  /:::/    /\:::\  /:::/    \::/    /
 *   \:::\   \:::\/:::/    /  \/____/ \:::\/:::/    /  \:::\/:::/    / \/____/
 *    \:::\   \::::::/    /            \::::::/    /    \::::::/    /
 *     \:::\   \::::/    /              \::::/    /      \::::/____/
 *      \:::\  /:::/    /               /:::/    /        \:::\    \
 *       \:::\/:::/    /               /:::/    /          \:::\    \
 *        \::::::/    /               /:::/    /            \:::\    \
 *         \::::/    /               /:::/    /              \:::\____\
 *          \::/____/                \::/    /                \::/    /
 *           ~~                       \/____/                  \/____/
 *
 */

import React, { Component } from "react";
import { IntlProvider, addLocaleData, injectIntl } from "react-intl";
import { Redirect, Route, Switch } from "react-router-dom";
import { ConnectedRouter } from "connected-react-router";
import connect from "react-redux/es/connect/connect";
import PropTypes from "prop-types";
import styled from "styled-components";
import locale_en from "react-intl/locale-data/en";
import locale_ms from "react-intl/locale-data/zh";
import locale_zh from "react-intl/locale-data/ms";
import messages_en from "./translations/en.json";
import messages_zh from "./translations/zh.json";
import messages_ms from "./translations/ms.json";
import MainPage from "./containers/MainPage";
import AuthPage from "./containers/AuthPage";
import NotFoundPage from "./containers/NotFoundPage";
import LanguagePage from "./containers/LanguagePage";
import RegisterPage from "./containers/RegisterPage";
import VerifyMobilePage from "./containers/VerifyMobilePage";
import ForgotPasswordPage from "./containers/ForgotPasswordPage";
import ResetPasswordSuccessPage from "./containers/ResetPasswordSuccessPage";
import * as selectors from "./selectors";
import * as globalAction from "./actions/global";
import IntlGlobalProvider from "./services/intlGlobalProvider";
import Theme from "./utils/Theme";
//todo fix this in production build
const localeData = {
  en: messages_en,
  zh: messages_zh,
  ms: messages_ms
};

// adding available languages
addLocaleData([...locale_en, ...locale_ms, ...locale_zh]);

// Define user's language. Different browsers have the user locale defined
// on different fields on the `navigator` object, so we make sure to account
// for these different by checking all of them
const language =
  (navigator.languages && navigator.languages[0]) ||
  navigator.language ||
  navigator.userLanguage ||
  "en";

// Split locales with a region code
const languageWithoutRegionCode = language.toLowerCase().split(/[_-]+/)[0];

// Try full locale, try locale without region code, fallback to 'en'
const messages =
  localeData[languageWithoutRegionCode] ||
  localeData[language] ||
  localeData.en;

const StyledContainer = styled.div`
  background: white;
  max-width: ${Theme.MaxWidth};
  overflow-x: hidden;
  min-height: 100%;
  width: 100%;
  margin: 0 auto;
  box-shadow: 5px 0 15px -4px #808080, -5px 0 8px -4px #808080;
`;

class App extends Component {
  constructor(...args) {
    super(...args);

    const { globalLanguage } = this.props;

    if (!globalLanguage) {
      this.onLanguageChange(language);
    }
  }

  onLanguageChange = language => {
    const { updateLanguage } = this.props;

    updateLanguage && updateLanguage(language);
  };

  render() {
    const { globalLanguage, history } = this.props;
    const currentLanguage = globalLanguage ? globalLanguage : language;
    const currentMessages = globalLanguage
      ? localeData[globalLanguage]
      : messages;

    return (
      <IntlProvider locale={currentLanguage} messages={currentMessages}>
        <IntlGlobalProvider>
          <ConnectedRouter history={history}>
            <StyledContainer>
              <Switch>
                <Route
                  path="/shop"
                  render={props => (
                    <MainPage
                      {...props}
                      changeLanguage={this.onLanguageChange}
                    />
                  )}
                />
                <Route
                  path="/language"
                  render={props => (
                    <LanguagePage
                      {...props}
                      changeLanguage={this.onLanguageChange}
                      currentLanguage={globalLanguage}
                    />
                  )}
                />
                <Route path="/verify" component={VerifyMobilePage} />
                <Route path="/forgot-password" component={ForgotPasswordPage} />
                <Route path="/register" component={RegisterPage} />
                <Route
                  path="/reset-password-successful"
                  component={ResetPasswordSuccessPage}
                />
                <Route path="/auth" component={AuthPage} />
                <Route exact path="/" render={() => <Redirect to="/shop" />} />
                <Route component={NotFoundPage} />
              </Switch>
            </StyledContainer>
          </ConnectedRouter>
        </IntlGlobalProvider>
      </IntlProvider>
    );
  }
}

AuthPage.propTypes = {
  history: PropTypes.object.isRequired,
  globalLanguage: PropTypes.string,
  updateLanguage: PropTypes.func
};

function mapStateToProps(state) {
  return {
    globalLanguage: selectors.getGlobalLanguage(state)
  };
}

function mapDispatchToProps(dispatch) {
  return {
    updateLanguage: language => dispatch(globalAction.updateLanguage(language))
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(App);



// WEBPACK FOOTER //
// ./src/App.js